import apiService from './api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import type { Club } from '@/types/api.types';
import type { ApiResponse } from '@/types/api';

// Используем тип ApiResponse из @/types/api

export interface CreateClubDTO {
    name: string;
    city: string;
    address: string;
    coachName: string;
    federationId?: number;
    managerIds: number[];
    phone?: string;
    email?: string;
    website?: string;
}

interface PaginatedResponse<T> {
    success: boolean;
    data: T[] | { rows: T[]; count: number }; // Поддержка обоих форматов
    total?: number;
    page?: number;
    limit?: number;
    message?: string;
}

interface GetClubsParams {
    page?: number;
    limit?: number;
    status?: string;
}

export const ClubService = {
    createClub: async (data: CreateClubDTO): Promise<Club> => {

        try {
            const { data: responseData } = await apiService.post<ApiResponse<Club>>(
                API_ENDPOINTS.CLUBS.CREATE,
                data
            );
            
            return responseData.data;
        } catch (error) {
            logger.error('Club creation error:', error);
            throw error;
        }
    },

    getClubs: async (params: GetClubsParams = {}): Promise<PaginatedResponse<Club>> => {
        
        try {
            const { data: responseData } = await apiService.get<any>(
                `${API_ENDPOINTS.CLUBS.BASE}`,
                {
                    params: {
                        page: params.page || 1,
                        limit: params.limit || 100,
                        status: params.status
                    }
                }
            );            
            
            // Нормализуем ответ: если data - это массив, оборачиваем его в формат с rows
            if (Array.isArray(responseData.data)) {
                return {
                    success: responseData.success ?? true,
                    data: {
                        rows: responseData.data,
                        count: responseData.total ?? responseData.data.length
                    },
                    total: responseData.total,
                    page: responseData.page,
                    limit: responseData.limit
                } as PaginatedResponse<Club>;
            }
            
            // Если уже в правильном формате, возвращаем как есть
            return responseData;
        } catch (error) {
            logger.error('Error fetching clubs:', error);
            throw error;
        }
    },

    updateClub: async (id: number, data: Partial<Club>): Promise<Club> => {
        
        try {
            const { data: responseData } = await apiService.put<ApiResponse<Club>>(
                API_ENDPOINTS.CLUBS.UPDATE(id),
                data
            );
            return responseData.data;
        } catch (error) {
            logger.error('Error updating club:', error);
            throw error;
        }
    },

    uploadLogo: async (id: number, file: File): Promise<string> => {
        
        try {
            const formData = new FormData();
            formData.append('logo', file);

            const { data: responseData } = await apiService.post<ApiResponse<{ logoUrl: string }>>(
                `${API_ENDPOINTS.CLUBS.BASE}/${id}/logo`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return responseData.data.logoUrl;
        } catch (error) {
            logger.error('Error uploading club logo:', error);
            throw error;
        }
    },

    deleteClub: async (id: number): Promise<void> => {
        
        try {
            await apiService.delete(API_ENDPOINTS.CLUBS.DELETE(id));            
        } catch (error) {
            logger.error('Error deleting club:', error);
            throw error;
        }
    },

    getClubById: async (id: number): Promise<Club> => {
        if (!id) {
            logger.error('Invalid club ID provided:', id);
            throw new Error('Invalid club ID');
        }

        try {
            const response = await apiService.get<ApiResponse<Club>>(
                API_ENDPOINTS.CLUBS.GET(id)
            );

            const { data: responseData } = response;

            // Проверяем, что ответ содержит данные клуба
            if (!responseData || !responseData.data || !responseData.data.id) {
                logger.error('Invalid club data received');
                throw new Error(`Club with ID ${id} not found or has invalid data`);
            }
            return responseData.data;
        } catch (error) {
            logger.error(`Error fetching club with ID ${id}:`, error);
            throw error;
        }
    }
};

export default ClubService;
